import React, { useState, useEffect, useRef } from "react";
import "./PerformanceManagement.css";
import { useNavigate } from 'react-router-dom';
import PerformanceHero from "./PerformanceHero";
import PerformanceFeatures from "./PerformanceFeatures";
import HRCopilotJobStatus from "./HRCopilotJobStatus";
import HRCopilotFeatures from "./HRCopilotFeatures";
import HRCopilotNewsSection from "./HRCopilotNewsSection";
import PerformanceWiseCode from './PerformanceWiseCode';



const VideoPopup = ({ isOpen, onClose, videoUrl }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
      }
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Add these functions to your component


  return (
    <div className="performance-video-popup-overlay" onClick={onClose}>
      <div className="performance-video-popup-content" onClick={(e) => e.stopPropagation()}>
        <button className="performance-video-popup-close" onClick={onClose}>
          ×
        </button>
        <div className="performance-video-container">
          <video ref={videoRef} controls autoPlay className="performance-demo-video">
            <source src={videoUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
        <div className="performance-video-popup-info">
          <h3>Performance Management Platform</h3>
          <p>See how our performance management system drives employee growth and organizational success</p>
        </div>
      </div>
    </div>
  );
};

const PerformanceManagement = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isVideoPopupOpen, setIsVideoPopupOpen] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const [scrollY, setScrollY] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
    const [isOpen, setIsOpen] = useState(false);


  const handleAccessRecruit = () => navigate('/access-recruit');
  const handleStory = () => navigate('/mission-vision');

  useEffect(() => {
    setIsVisible(true);
    
    // Auto rotate features
    const interval = setInterval(() => {
      setActiveFeature(prev => (prev + 1) % 6);
    }, 4000);
    
    // Scroll progress
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrollTop / docHeight) * 100;
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const demoVideoUrl = "https://assets.mixkit.co/videos/preview/mixkit-businessman-analyzing-data-5086-large.mp4";
  const openVideoPopup = () => setIsOpen(true);
  const closeVideoPopup = () => setIsOpen(false);

  const handlePerformanceDemo = () => navigate('/payment');
  

const openPerformanceDemo = () => navigate('/access-recruit')
  // Open performance demo video/modal
 

const handleExploreIntegrations = () => {
  // Handle explore integrations click
  console.log('Exploring integrations');
  // Add your actual logic here, such as:
  // - Redirect to integrations page
  // - Open integrations modal
  // - Scroll to integrations section
  // - etc.
};
 const hrIntegrations = [
    'LinkedIn Recruiter', 'Greenhouse', 'Lever', 'Workday', 'BambooHR', 'ADP', 
    'Slack', 'Microsoft Teams', 'Zoom', 'Google Workspace', 'Office 365', 'Jira'
  ];
  const features = [
    {
      icon: "🎯",
      title: "Goal Setting & Tracking",
      description: "Set SMART goals and track progress with real-time updates and AI-powered insights"
    },
    {
      icon: "📊",
      title: "Performance Analytics",
      description: "Comprehensive dashboards with predictive analytics and performance trends"
    },
    {
      icon: "🔄",
      title: "Continuous Feedback",
      description: "360-degree feedback system with peer recognition and manager coaching"
    },
    {
      icon: "📈",
      title: "Career Development",
      description: "Personalized growth paths and skill development recommendations"
    },
    {
      icon: "🤝",
      title: "1-on-1 Meetings",
      description: "Structured meeting agendas with action items and follow-up tracking"
    },
    {
      icon: "🏆",
      title: "Recognition & Rewards",
      description: "Automated recognition programs and performance-based rewards"
    }
  ];

  const cycles = [
    {
      id: 1,
      phase: "Planning & Goal Setting",
      time: "Quarter 1: Weeks 1–2",
      icon: "🎯",
      title: "Strategic Objective",
      steps: [
        "AI-powered goal recommendations based",
        "OKR framework integration",
        "Skill gap analysis before goal setting",
        "Cross-team alignment",
        "Smart calibration using industry benchmarks"
      ],
      metrics: "95% goal clarity score",
      insight: "Predictive goal success probability"
    },
    {
      id: 2,
      phase: "Continuous Check-ins",
      time: "Weekly — Throughout the Year",
      icon: "📊",
      title: "Real-Time Tracking",
      steps: [
        "Automated progress tracking",
        "Sentiment analysis in conversations",
        "AI-generated talking points",
        "Continuous feedback tracking",
        "Coaching recommendations"
      ],
      metrics: "42% improvement in meaningful conversations",
      insight: "AI detects early risk indicators"
    },
    {
      id: 3,
      phase: "Performance Review",
      time: "Quarter 4: Weeks 10–12",
      icon: "🏆",
      title: "360° Assessment",
      steps: [
        "Automated review preparation",
        "Bias detection engine",
        "AI-generated review narrative",
        "Comparative peer benchmarking",
        "Promotion recommendation engine"
      ],
      metrics: "89% faster review cycle",
      insight: "Predictive performance forecasting"
    }
  ];

  const performanceCycles = [
    {
      phase: "Goal Setting",
      title: "Quarterly Objectives & Key Results",
      steps: ["Goal Alignment", "OKR Setting", "Development Planning", "Stakeholder Buy-in"],
      time: "Quarter Start"
    },
    {
      phase: "Check-ins",
      title: "Continuous Progress Monitoring",
      steps: ["Weekly Updates", "Feedback Exchange", "Coaching Sessions", "Progress Reviews"],
      time: "Ongoing"
    },
    {
      phase: "Review",
      title: "Comprehensive Performance Review",
      steps: ["Self-Assessment", "Peer Feedback", "Manager Evaluation", "Calibration Sessions"],
      time: "Quarter End"
    }
  ];

  return (
    <div className="performance-container">
      {/* Scroll Progress Bar */}
      {/* <div className="performance-scroll-progress" style={{width: `${scrollProgress}%`}}></div> */}

      {/* Video Popup */}
      <VideoPopup 
        isOpen={isVideoPopupOpen} 
        onClose={closeVideoPopup} 
        videoUrl={demoVideoUrl}
      />

      {/* Enhanced Animated Background */}
      <div className="performance-background-animation">
        {/* <div className="performance-floating-shape performance-shape-1"></div>
        <div className="performance-floating-shape performance-shape-2"></div>
        <div className="performance-floating-shape performance-shape-3"></div>
        <div className="performance-floating-shape performance-shape-4"></div>
        <div className="performance-floating-shape performance-shape-5"></div> */}
        
        {/* Animated Gradient Orbs */}
        {/* <div className="performance-gradient-orb performance-orb-1"></div>
        <div className="performance-gradient-orb performance-orb-2"></div>
        <div className="performance-gradient-orb performance-orb-3"></div>
         */}
        {/* SVG Background Animation */}
        <svg className="performance-bg-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <linearGradient id="performance-gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="rgba(74, 144, 226, 0.08)" />
              <stop offset="50%" stopColor="rgba(120, 119, 216, 0.05)" />
              <stop offset="100%" stopColor="rgba(226, 74, 144, 0.08)" />
            </linearGradient>
            <linearGradient id="performance-gradient2" x1="100%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(120, 119, 216, 0.06)" />
              <stop offset="50%" stopColor="rgba(119, 216, 216, 0.04)" />
              <stop offset="100%" stopColor="rgba(74, 226, 144, 0.06)" />
            </linearGradient>
            <pattern id="performance-grid" width="10" height="10" patternUnits="userSpaceOnUse">
              <path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100" height="100" fill="url(#performance-grid)" />
          <path 
            d="M0,15 Q30,5 60,25 T100,35 L100,100 L0,100 Z" 
            fill="url(#performance-gradient1)"
            className="performance-svg-path-1"
          />
          <path 
            d="M0,65 Q40,45 80,70 T100,85 L100,100 L0,100 Z" 
            fill="url(#performance-gradient2)"
            opacity="0.8"
            className="performance-svg-path-2"
          />
        </svg>
      </div>

<PerformanceHero/>

<PerformanceWiseCode/>

<HRCopilotFeatures/>

{/* <PerformanceFeatures/> */}
<HRCopilotJobStatus/>


<HRCopilotNewsSection/>


    </div>
  );
};

export default PerformanceManagement;